import { useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import useAccessValidation from '@/hooks/useAccessValidation';
import useProfile from '@/hooks/useProfileV2';
import { useAuth } from '@/providers/SupabaseAuthProvider';
import { Paths } from '@/types/routes';

/**
 * Normalize the path for permission checking:
 * /talentx/* → /hiring/* and /peoplex/* → /people/*
 * This avoids requiring backend permission changes.
 */
const normalizePathForPermissions = (path: string): string =>
  path.replace(/^\/talentx(\/|$)/, '/hiring$1').replace(/^\/peoplex(\/|$)/, '/people$1');

const RedirectHandler = function ({ children }: { children: JSX.Element }): JSX.Element | null {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { profile } = useProfile();

  // Normalize the path so permission checks use the legacy prefix
  const normalizedPath = normalizePathForPermissions(pathname);
  const doesUserHavePermission = useAccessValidation({ pagePath: normalizedPath });

  const isUserAllowed = useMemo(() => {
    const allowedPaths = [
      Paths.HOME.toString(),
      Paths.NOT_INVITED.toString(),
      Paths.NOT_ALLOWED.toString(),
      Paths.ONBOARDING.toString(),
    ];

    if (allowedPaths.includes(pathname)) {
      return true;
    }

    return doesUserHavePermission;
  }, [doesUserHavePermission, pathname]);

  if (isUserAllowed !== undefined && !isUserAllowed) {
    const redirectPath = isAuthenticated ? Paths.NOT_ALLOWED : Paths.LOGIN;
    return <Navigate to={redirectPath} replace />;
  }

  if (profile.isNotInvited) return <Navigate to={Paths.NOT_INVITED} replace />;

  // Redirect to onboarding if user hasn't completed their profile
  if (profile.needsOnboarding && pathname !== Paths.ONBOARDING.toString()) {
    return <Navigate to={Paths.ONBOARDING} replace />;
  }

  return children;
};

export default RedirectHandler;
